import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showErrorAlert } from "../../utils/showErrorAlert";
import { extractIdFromIri } from "../../utils/extractFromIri";
import { showSuccessToast } from "../../utils/showToastAlert";

/**
 * @param {object} deps
 * @param {(url: string, options: any) => Promise<any>} deps.callApiWithWorker
 * @param {(key: string) => string} deps.t
 */
export function usePutDataService({ callApiWithWorker, t }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateDataService"],

    mutationFn: async ({ id, payload }) => {
      if (!id) {
        throw new Error("DataService id is required to update a DataService");
      }

      const normalizedId = extractIdFromIri(id);

      const res = await callApiWithWorker(`/dataservices/${normalizedId}`, {
        method: "PUT",
        body: payload,
      });

      const statusCode = res?.statusCode ?? res?.data?.statusCode;
      const bodyString = res?.body ?? res?.data?.body;

      if (statusCode && statusCode >= 400) {
        let message = "DataService update failed";
        try {
          const parsed =
            typeof bodyString === "string"
              ? JSON.parse(bodyString)
              : bodyString;
          message = parsed?.message ?? message;
        } catch {}
        throw new Error(message);
      }

      return bodyString;
    },

    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["DataService", id] });

      queryClient.invalidateQueries({ queryKey: ["dataServices"] });

      showSuccessToast(t("updateSubMessage"), {
        title: t("updateMessage"),
      });
    },

    onError: (error) => {
      showErrorAlert(error);
    },
  });
}
